import { useState, useMemo, useEffect } from "react"
import leftIcon from "assets/svg/chevron-left.svg"
import rightIcon from "assets/svg/chevron-right.svg"
import EmptyImg from "../../../../assets/illustrations/timesheet-empty-img.svg"
import swapIcon from "assets/svg/arrow-left-right.svg"
import {
  startOfWeek,
  addDays,
  toKeyDate,
  formatDate,
  formatSentenceCase,
} from "../utils"
import { Divider, Skeleton } from "antd"
import { convertTo12Hr } from "../utils"
import { useScreenBreakpoints } from "hooks/hook"
import { useGet } from "utils/useFetch"
import {
  IShiftSetupRes,
  IShiftPlan,
  IShiftAssignmentRes,
} from "../interface/shiftData"
import ShiftSwapModal from "./ShiftSwapModal"
import { ShiftCell } from "./ShiftCell"
import { ClipboardDocumentIcon } from "@heroicons/react/24/outline"
import { toast } from "react-toastify"

const SHIFT_CALENDAR_GRID_COLUMNS = "100px repeat(7, minmax(148px, 1fr))"

const AssignedShiftsCalendar = ({
  shiftSetupData,
  isLoadingShiftSetup,
  planType,
  dutyLocationId,
}: {
  shiftSetupData: IShiftSetupRes | undefined
  isLoadingShiftSetup: boolean
  planType: (isAnyTime: boolean, allowSwapping: boolean) => void
  dutyLocationId: number | null
}) => {
  const [showSwapShiftModal, setShowSwapShiftModal] = useState(false)
  const [currentWeekStart, setCurrentWeekStart] = useState(
    startOfWeek(new Date())
  )
  const [selectedShiftAssignment, setSelectedShiftAssignment] = useState<{
    assignmentId: number | undefined
    shiftPlanId: number | undefined
    shiftPeriodId: number | undefined
    shiftPeriodTitle: string | undefined
    date: string | null | undefined
  } | null>(null)

  const shiftPlans: IShiftPlan[] = shiftSetupData?.data?.shiftPlans ?? []
  const isMobile = useScreenBreakpoints("sm")
  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }).map((_, i) => addDays(currentWeekStart, i))
  }, [currentWeekStart])

  const startDate = toKeyDate(weekDays[0])
  const endDate = toKeyDate(weekDays[6])

  const queryKey = dutyLocationId
    ? `/v1/time-attendance/employee/shift-assignments?startDate=${startDate}&endDate=${endDate}&limit=50&dutyLocationId=${dutyLocationId}`
    : `/v1/time-attendance/employee/shift-assignments?startDate=${startDate}&endDate=${endDate}&limit=50`

  const { data: shiftData, isLoading } = useGet<IShiftAssignmentRes>({
    url: queryKey,
    instanceKey: "ewaAxiosInstance",
  })

  // ─── Group assignments by shiftPeriodId ──────────────────────────────

  const groupedShiftData = useMemo(() => {
    return shiftData?.data ?? []
  }, [shiftData])

  // ─── Current plan ─────────────────────────────────────────────────────────

  const currentPlanId =
    shiftSetupData?.data?.shiftAssignment?.[0]?.shiftPlanId ??
    groupedShiftData?.[0]?.shiftAssignments?.[0]?.shiftPlanId
  const currentPlan = shiftPlans?.find((plan) => plan.id === currentPlanId)
  const currentPlanType = currentPlan?.type
  const currentPlanName = currentPlan?.title
  const allowSwapping = shiftPlans.some((p) => p.allowSwapping)
  const isAnyTimePlan =
    currentPlanType === "flexible" || currentPlanType === "anytime"

  useEffect(() => {
    if (currentPlanType !== undefined && !isLoading) {
      planType(isAnyTimePlan, allowSwapping ?? false)
    }
  }, [currentPlanType, isLoading, allowSwapping])

  // ─── Weekly limit ─────────────────────────────────────────────────────────

  const weeklyLimit = useMemo(() => {
    if (!isAnyTimePlan) return null
    for (const shift of groupedShiftData) {
      for (const assignment of shift.shiftAssignments) {
        if (assignment.weeklyLimit != null) return assignment.weeklyLimit
      }
    }
    return null
  }, [groupedShiftData, isAnyTimePlan])

  // ─── Assigned map (rostered) ──────────────────────────────────────────────

  const assignedMap = useMemo(() => {
    if (isAnyTimePlan) return new Set<string>()

    return new Set(
      groupedShiftData.flatMap((shift) =>
        shift.shiftAssignments.flatMap((a) => {
          if (!a.dayOfWeek) return []
          return weekDays
            .filter(
              (day) =>
                formatDate(day, { weekday: "long" }).toLowerCase() ===
                a.dayOfWeek
            )
            .map((day) => `${toKeyDate(day)}-${a.shiftPeriodId}`)
        })
      )
    )
  }, [groupedShiftData, isAnyTimePlan, weekDays])

  // ─── Eligible map (anytime/flexible) ─────────────────────────────────────

  const eligibleMap = useMemo(() => {
    if (!isAnyTimePlan) return new Map<number, Set<string>>()
    const map = new Map<number, Set<string>>()

    groupedShiftData.forEach((shift) => {
      const eligibleDays = new Set<string>()

      shift.shiftAssignments.forEach((a) => {
        if (a.isAnyDayPermitted && a.shiftTable) {
          const start = new Date(a.shiftTable.startDate)
          const end = new Date(a.shiftTable.endDate)
          const cursor = new Date(start)

          while (cursor <= end) {
            eligibleDays.add(
              formatDate(cursor, { weekday: "long" }).toLowerCase()
            )
            cursor.setDate(cursor.getDate() + 1)
          }
        } else if (a.dayOfWeek) {
          eligibleDays.add(a.dayOfWeek.toLowerCase())
        }
      })

      map.set(shift.shiftPeriodId, eligibleDays)
    })

    return map
  }, [groupedShiftData, isAnyTimePlan])

  const nextWeek = () => setCurrentWeekStart(addDays(currentWeekStart, 7))
  const prevWeek = () => setCurrentWeekStart(addDays(currentWeekStart, -7))

  const handleCopyId = (id: number | undefined) => {
    navigator.clipboard
      .writeText(String(id))
      .then(() => toast.success("Shift ID copied!"))
      .catch(() => toast.error("Failed to copy ID"))
  }

  return (
    <div className="bg-white p-4 my-2 mt-1">
      <div className="block md:flex lg:flex items-center justify-between">
        <div className="block lg:flex items-center gap-2 mb-2 md:mb-0">
          <h5 className="text-base text-padeBlack font-semibold shrink-0">
            Showing assigned shifts for:
          </h5>
          <div className="mt-1 lg:mt-0">
            <div className="flex items-center gap-2">
              <span
                className="bg-slate-100 flex items-center justify-center p-1 rounded-full"
                onClick={prevWeek}
              >
                <img
                  src={leftIcon}
                  width={15}
                  height={15}
                />
              </span>
              <span className="text-sm">
                {formatDate(weekDays[0], { month: "short", day: "numeric" })} -{" "}
                {formatDate(weekDays[6], { month: "short", day: "numeric" })}
              </span>
              <span
                className="bg-slate-100 flex items-center justify-center p-1 rounded-full"
                onClick={nextWeek}
              >
                <img
                  src={rightIcon}
                  width={15}
                  height={15}
                />
              </span>
            </div>
          </div>
        </div>
        <Divider className="block md:hidden" />
        <div className="-m-2 lg:m-0">
          {isLoading ? (
            <div className="flex items-center gap-1">
              <p className="text-slate-400 flex items-center gap-1">
                [{" "}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="size-6 w-4 h-4"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5m-9-6h.008v.008H12v-.008ZM12 15h.008v.008H12V15Zm0 2.25h.008v.008H12v-.008ZM9.75 15h.008v.008H9.75V15Zm0 2.25h.008v.008H9.75v-.008ZM7.5 15h.008v.008H7.5V15Zm0 2.25h.008v.008H7.5v-.008Zm6.75-4.5h.008v.008h-.008v-.008Zm0 2.25h.008v.008h-.008V15Zm0 2.25h.008v.008h-.008v-.008Zm2.25-4.5h.008v.008H16.5v-.008Zm0 2.25h.008v.008H16.5V15Z"
                  />
                </svg>{" "}
                ]
              </p>
            </div>
          ) : (
            <div className="block lg:flex items-center gap-2 text-sm mr-2 lg:mr-0">
              {isMobile && isAnyTimePlan && weeklyLimit != null && (
                <strong>{weeklyLimit} Shifts required / week</strong>
              )}
              <div className="flex gap-1">
                <strong>
                  {!isAnyTimePlan && formatDate(weekDays[0], { month: "long" })}{" "}
                  <span>{formatSentenceCase(currentPlanName)}</span>
                </strong>
                <p className="text-slate-400 flex items-center gap-1">
                  [{" "}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="size-6 w-4 h-4"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5m-9-6h.008v.008H12v-.008ZM12 15h.008v.008H12V15Zm0 2.25h.008v.008H12v-.008ZM9.75 15h.008v.008H9.75V15Zm0 2.25h.008v.008H9.75v-.008ZM7.5 15h.008v.008H7.5V15Zm0 2.25h.008v.008H7.5v-.008Zm6.75-4.5h.008v.008h-.008v-.008Zm0 2.25h.008v.008h-.008V15Zm0 2.25h.008v.008h-.008v-.008Zm2.25-4.5h.008v.008H16.5v-.008Zm0 2.25h.008v.008H16.5V15Z"
                    />
                  </svg>
                  <span>{formatSentenceCase(currentPlanType)}</span> plan ]
                </p>
              </div>
              {!isMobile && isAnyTimePlan && weeklyLimit != null && (
                <strong>{weeklyLimit} Shifts required / week</strong>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mt-5">
        <div
          className="border border-gray-300 rounded-t-lg overflow-x-auto relative z-0"
          style={{ scrollbarWidth: "none" }}
        >
          <div className="w-max min-w-full">
            <ul
              className="bg-[#F8FAFC] border-b py-3 px-2 sticky top-0 z-20 grid w-full"
              style={{ gridTemplateColumns: SHIFT_CALENDAR_GRID_COLUMNS }}
            >
              <li className="sticky left-0 bg-[#F8FAFC] z-30" />
              {weekDays.map((day) => (
                <li
                  key={day.toISOString()}
                  className="text-slate-500 min-w-0 pl-2"
                >
                  {formatDate(day, { weekday: "long" })}
                </li>
              ))}
            </ul>

            {isLoading || isLoadingShiftSetup ? (
              <div
                className="grid items-center border-b border-gray-300 divide-x divide-dashed divide-gray-300 w-full"
                style={{ gridTemplateColumns: SHIFT_CALENDAR_GRID_COLUMNS }}
              >
                {Array.from({ length: 8 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-[148px] flex items-center justify-center px-2"
                  >
                    <Skeleton.Button
                      active
                      shape="square"
                      style={{ width: "100%", height: 56, borderRadius: 6 }}
                    />
                  </div>
                ))}
              </div>
            ) : groupedShiftData.length === 0 ? (
              <div className="mx-auto">
                <img
                  src={EmptyImg}
                  className="w-full object-contain"
                />
              </div>
            ) : (
              groupedShiftData.map((shift) => (
                <div
                  key={shift.shiftPeriodId}
                  className="grid items-stretch border-b last:border-none border-gray-300 divide-x divide-dashed divide-gray-300 bg-white w-full"
                  style={{ gridTemplateColumns: SHIFT_CALENDAR_GRID_COLUMNS }}
                >
                  <div className="font-circular sticky left-0 bg-white z-10 py-3 px-3 h-[148px] flex flex-col justify-center">
                    <p className="font-medium text-sm pb-2">
                      {shift.shiftPeriod?.title}
                    </p>
                    <small className="text-slate-400 border-l-2 border-l-slate-200 pl-1.5 flex flex-col">
                      <span>{convertTo12Hr(shift.shiftPeriod?.startTime)}</span>
                      <span>{convertTo12Hr(shift.shiftPeriod?.endTime)}</span>
                    </small>
                    <p className="text-slate-400 mt-1">
                      ID:{" "}
                      <span className="text-[#0F172B]">
                        {shift.shiftPeriod?.id}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleCopyId(shift.shiftPeriod?.id)}
                        className="text-slate-500 hover:text-slate-900 pl-[2px] mt-1"
                        aria-label="Copy shift period ID"
                      >
                        <ClipboardDocumentIcon className="w-3.5 h-3.5" />
                      </button>
                    </p>
                  </div>

                  {weekDays.map((day) => {
                    if (isAnyTimePlan) {
                      const dayName = formatDate(day, {
                        weekday: "long",
                      }).toLowerCase()
                      const eligibleDays = eligibleMap.get(shift.shiftPeriodId)
                      const isEligible = eligibleDays?.has(dayName) ?? false

                      return (
                        <ShiftCell
                          key={`${toKeyDate(day)}-${shift.shiftPeriodId}`}
                          isActive={isEligible}
                          label="Eligible"
                        />
                      )
                    }

                    const key = `${toKeyDate(day)}-${shift.shiftPeriodId}`
                    const isAssigned = assignedMap.has(key)
                    const shiftAssignment = shift.shiftAssignments.find(
                      (a) =>
                        a.dayOfWeek ===
                        formatDate(day, { weekday: "long" }).toLowerCase()
                    )

                    return (
                      <ShiftCell
                        key={key}
                        isActive={isAssigned}
                        label="Assigned"
                      >
                        {shiftPlans.find((p) => p.id === shiftAssignment?.shiftPlanId)?.allowSwapping && (
                          <button
                            onClick={() => {
                              setSelectedShiftAssignment({
                                assignmentId: shiftAssignment?.id,
                                shiftPlanId: shiftAssignment?.shiftPlanId,
                                shiftPeriodId: shiftAssignment?.shiftPeriodId,
                                shiftPeriodTitle: shift.shiftPeriod?.title,
                                date: shiftAssignment?.date ?? toKeyDate(day),
                              })
                              setShowSwapShiftModal(true)
                            }}
                            className="flex w-fit items-center justify-center gap-0.5 whitespace-nowrap rounded-md border border-[#CAD5E2] bg-[#E2E8F0] px-2 py-1 text-xs font-medium leading-[1.5] text-[#0F172B] hover:border-[#0F172B]"
                          >
                            <img
                              width={12}
                              height={12}
                              src={swapIcon}
                              alt="swap"
                            />
                            Swap shift
                          </button>
                        )}
                      </ShiftCell>
                    )
                  })}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {!isAnyTimePlan && (
        <ShiftSwapModal
          assignedMap={assignedMap}
          shiftPlan={currentPlan}
          shiftSetupData={shiftSetupData}
          setShowSwapShiftModal={setShowSwapShiftModal}
          selectedShiftAssignment={selectedShiftAssignment}
          open={showSwapShiftModal}
        />
      )}
    </div>
  )
}

export { AssignedShiftsCalendar }
